import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Loader2, RefreshCw, HeartPulse } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { MetricChart } from "@/components/charts";

const HEALTH_COLOR = "#8FB9B2";

function fmtValue(v, unit) {
  if (v == null) return "—";
  const num = typeof v === "number" ? new Intl.NumberFormat().format(v) : v;
  return unit ? `${num} ${unit}` : num;
}

function ago(ts) {
  if (!ts) return "never";
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

function DeviceCard({ device, version }) {
  const [series, setSeries] = useState(null);
  const [selected, setSelected] = useState(device.measurements[0]?.key || null);

  useEffect(() => {
    let active = true;
    api
      .get(`/health/devices/${device.id}/readings`, { params: { days: 365 } })
      .then(({ data }) => active && setSeries(data))
      .catch(() => active && setSeries({}));
    return () => {
      active = false;
    };
  }, [device.id, version]);

  const sel = device.measurements.find((m) => m.key === selected);

  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid={`health-device-card-${device.id}`}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-serif text-xl text-foreground">{device.name}</h3>
          <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground mt-0.5">
            {device.manufacturer}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
        {device.measurements.map((m) => {
          const latest = device.latest?.[m.key];
          return (
            <div key={m.key} className="rounded-lg border border-border/70 bg-secondary/30 p-3" data-testid={`health-tile-${device.id}-${m.key}`}>
              <p className="text-xs text-muted-foreground mb-1">{m.label}</p>
              <p className="text-lg font-medium text-foreground">{fmtValue(latest?.value, m.unit)}</p>
              <p className="text-[11px] text-muted-foreground/70 mt-0.5">{ago(latest?.timestamp)}</p>
            </div>
          );
        })}
      </div>

      {device.measurements.length > 0 && (
        <div>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {device.measurements.map((m) => (
              <button
                key={m.key}
                onClick={() => setSelected(m.key)}
                data-testid={`health-metric-select-${device.id}-${m.key}`}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors duration-200 ${
                  selected === m.key
                    ? "text-background border-transparent"
                    : "bg-white/70 text-foreground/80 border-input hover:border-foreground/40"
                }`}
                style={selected === m.key ? { backgroundColor: HEALTH_COLOR } : {}}
              >
                {m.label}
              </button>
            ))}
          </div>
          {series === null ? (
            <div className="h-[200px] flex items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <MetricChart
              data={series[selected] || []}
              unit={sel?.unit || ""}
              color={HEALTH_COLOR}
              testid={`health-chart-${device.id}`}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default function HealthDashboard() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [version, setVersion] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/health/devices");
      setDevices(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const syncAll = async () => {
    if (devices.length === 0) return;
    setSyncing(true);
    let ok = 0;
    let failed = 0;
    for (const d of devices) {
      try {
        await api.post(`/health/devices/${d.id}/sync`);
        ok += 1;
      } catch (e) {
        failed += 1;
        toast.error(`${d.name}: ${formatApiError(e)}`);
      }
    }
    if (ok > 0) toast.success(`Synced ${ok} device${ok > 1 ? "s" : ""}`);
    setSyncing(false);
    await load();
    setVersion((v) => v + 1);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="health-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-muted-foreground">
          {devices.length} device{devices.length === 1 ? "" : "s"} connected
        </p>
        <button
          onClick={syncAll}
          disabled={syncing || devices.length === 0}
          className="flex items-center gap-2 px-4 py-2.5 rounded-md font-medium text-[#2C2C2C] disabled:opacity-50 transition-opacity duration-200"
          style={{ backgroundColor: HEALTH_COLOR }}
          data-testid="health-sync-all-btn"
        >
          {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Sync all devices
        </button>
      </div>

      {devices.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card p-12 text-center" data-testid="health-empty">
          <HeartPulse className="w-8 h-8 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-foreground font-medium mb-1">No devices yet</p>
          <p className="text-sm text-muted-foreground mb-4">Add a device to start tracking your health readings.</p>
          <Link
            to="/pillar/health/my-health/devices"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md font-medium text-[#2C2C2C]"
            style={{ backgroundColor: HEALTH_COLOR }}
            data-testid="health-empty-add-link"
          >
            Add a device
          </Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          {devices.map((d) => (
            <DeviceCard key={d.id} device={d} version={version} />
          ))}
        </div>
      )}
    </div>
  );
}
